const DIRECT_UPLOAD_EXTENSIONS: Record<string, string> = {
  "audio/webm": "webm",
  "audio/ogg": "ogg",
  "audio/wav": "wav",
  "audio/wave": "wav",
  "audio/x-wav": "wav",
  "audio/mpeg": "mp3",
  "audio/mp3": "mp3",
};

const WAV_FALLBACK_TYPES = new Set([
  "audio/mp4",
  "audio/x-m4a",
  "audio/aac",
]);

export interface PreparedAudioUpload {
  blob: Blob;
  mimeType: string;
  extension: string;
}

type BlobTranscoder = (blob: Blob) => Promise<Blob>;

export async function prepareRecordedAudioForUpload(
  blob: Blob,
  mimeType?: string,
  transcodeToWav: BlobTranscoder = transcodeBlobToWav,
): Promise<PreparedAudioUpload> {
  const normalizedMimeType = normalizeAudioMimeType(mimeType || blob.type) || "audio/webm";
  const directExtension = DIRECT_UPLOAD_EXTENSIONS[normalizedMimeType];
  if (directExtension) {
    return { blob, mimeType: normalizedMimeType, extension: directExtension };
  }
  if (WAV_FALLBACK_TYPES.has(normalizedMimeType)) {
    const wavBlob = await transcodeToWav(blob);
    return { blob: wavBlob, mimeType: "audio/wav", extension: "wav" };
  }
  return {
    blob,
    mimeType: normalizedMimeType,
    extension: extensionFromMimeType(normalizedMimeType),
  };
}

export function normalizeAudioMimeType(mimeType?: string): string {
  return mimeType?.split(";")[0]?.trim().toLowerCase() || "";
}

async function transcodeBlobToWav(blob: Blob): Promise<Blob> {
  const AudioContextCtor = getAudioContextCtor();
  if (!AudioContextCtor) {
    throw new Error("AudioContext unavailable for WAV transcode");
  }
  const audioContext = new AudioContextCtor();
  try {
    const decoded = await audioContext.decodeAudioData(await blob.arrayBuffer());
    const mono = mixToMono(decoded);
    return new Blob([encodeWavPcm16(mono, decoded.sampleRate)], {
      type: "audio/wav",
    });
  } finally {
    await audioContext.close().catch(() => undefined);
  }
}

function getAudioContextCtor(): typeof AudioContext | undefined {
  if (typeof window === "undefined") return undefined;
  return window.AudioContext || (
    window as Window & { webkitAudioContext?: typeof AudioContext }
  ).webkitAudioContext;
}

function mixToMono(audioBuffer: AudioBuffer): Float32Array {
  if (audioBuffer.numberOfChannels === 1) {
    return audioBuffer.getChannelData(0).slice();
  }
  const mono = new Float32Array(audioBuffer.length);
  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel += 1) {
    const samples = audioBuffer.getChannelData(channel);
    for (let index = 0; index < samples.length; index += 1) {
      mono[index] += samples[index] / audioBuffer.numberOfChannels;
    }
  }
  return mono;
}

function encodeWavPcm16(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let index = 0; index < samples.length; index += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[index]));
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    offset += 2;
  }
  return buffer;
}

function writeAscii(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function extensionFromMimeType(mimeType: string): string {
  const subtype = mimeType.split("/")[1] || "bin";
  return subtype.split(".").at(-1)?.split("+")[0] || "bin";
}
